import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import {
  X, Plus, Award, Calendar, Upload, FileText, Trash2, Edit3,
  AlertTriangle, CheckCircle, Clock, Download, Eye,
} from 'lucide-react';

// ── helpers ────────────────────────────────────────────────
function getStatus(expiryDate) {
  if (!expiryDate) return 'no-expiry';
  const now = new Date();
  const exp = new Date(expiryDate);
  const daysLeft = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
  if (daysLeft < 0) return 'expired';
  if (daysLeft <= 30) return 'expiring';
  return 'valid';
}

function statusBadge(status, t) {
  const map = {
    valid: { bg: 'bg-green-500/20 text-green-400', icon: <CheckCircle size={12} />, label: t('accreditations.valid') },
    expiring: { bg: 'bg-amber-500/20 text-amber-400', icon: <Clock size={12} />, label: t('accreditations.expiringSoon') },
    expired: { bg: 'bg-red-500/20 text-red-400', icon: <AlertTriangle size={12} />, label: t('accreditations.expired') },
    'no-expiry': { bg: 'bg-blue-500/20 text-blue-400', icon: <CheckCircle size={12} />, label: t('accreditations.noExpiry') },
  };
  const s = map[status] || map['no-expiry'];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.bg}`}>
      {s.icon} {s.label}
    </span>
  );
}

function daysUntilExpiry(expiryDate) {
  if (!expiryDate) return null;
  return Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
}

// ── AccreditationsModal ────────────────────────────────────
export default function AccreditationsModal({ staff, companyId, onClose }) {
  const { t } = useTranslation();
  const [accreditations, setAccreditations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (!companyId || !staff?.id) return;
    const q = query(
      collection(db, 'companies', companyId, 'staffAccreditations'),
      where('userId', '==', staff.id),
    );
    const unsub = onSnapshot(q, (snap) => {
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => {
        const sa = getStatus(a.expiryDate);
        const sb = getStatus(b.expiryDate);
        const order = { expired: 0, expiring: 1, valid: 2, 'no-expiry': 3 };
        return (order[sa] ?? 4) - (order[sb] ?? 4);
      });
      setAccreditations(items);
      setLoading(false);
    }, (err) => {
      console.error('Error loading accreditations:', err);
      setLoading(false);
    });
    return unsub;
  }, [companyId, staff?.id]);

  const handleDelete = async (accred) => {
    if (!window.confirm(t('accreditations.confirmDelete'))) return;
    try {
      await deleteDoc(doc(db, 'companies', companyId, 'staffAccreditations', accred.id));
      // delete file from storage if exists
      if (accred.filePath) {
        try { await deleteObject(ref(storage, accred.filePath)); } catch (_) {}
      }
    } catch (err) {
      console.error('Error deleting accreditation:', err);
      alert(t('accreditations.deleteFailed'));
    }
  };

  const expired = accreditations.filter(a => getStatus(a.expiryDate) === 'expired').length;
  const expiring = accreditations.filter(a => getStatus(a.expiryDate) === 'expiring').length;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-slate-800 border border-white/10 rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10 flex-shrink-0">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Award size={22} className="text-primary" />
                {t('accreditations.title')}
              </h2>
              <p className="text-gray-400 text-sm mt-1">
                {staff?.displayName || staff?.username} — {accreditations.length} {t('accreditations.certificates')}
                {expired > 0 && <span className="text-red-400 ml-2">({expired} {t('accreditations.expired')})</span>}
                {expiring > 0 && <span className="text-amber-400 ml-2">({expiring} {t('accreditations.expiringSoon')})</span>}
              </p>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded text-gray-300">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Add button */}
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-white/20 rounded-xl text-gray-400 hover:border-primary/50 hover:text-primary transition-colors"
          >
            <Plus size={18} /> {t('accreditations.add')}
          </button>

          {loading ? (
            <div className="text-center py-8 text-gray-400">{t('common.loading')}</div>
          ) : accreditations.length === 0 ? (
            <div className="text-center py-8">
              <Award size={40} className="text-gray-600 mx-auto mb-2" />
              <p className="text-gray-400">{t('accreditations.noAccreditations')}</p>
              <p className="text-gray-500 text-sm mt-1">{t('accreditations.noAccreditationsHint')}</p>
            </div>
          ) : (
            accreditations.map(accred => {
              const status = getStatus(accred.expiryDate);
              const days = daysUntilExpiry(accred.expiryDate);
              return (
                <div
                  key={accred.id}
                  className={`bg-white/5 border rounded-xl p-4 ${
                    status === 'expired' ? 'border-red-500/30' :
                    status === 'expiring' ? 'border-amber-500/30' : 'border-white/10'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-white">{accred.name}</h3>
                        {statusBadge(status, t)}
                      </div>
                      {accred.issuer && (
                        <p className="text-sm text-gray-400 mt-1">{t('accreditations.issuer')}: {accred.issuer}</p>
                      )}
                      {accred.certificateNumber && (
                        <p className="text-sm text-gray-400">{t('accreditations.certNumber')}: {accred.certificateNumber}</p>
                      )}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                        {accred.issueDate && (
                          <span className="flex items-center gap-1">
                            <Calendar size={12} /> {t('accreditations.issued')}: {new Date(accred.issueDate).toLocaleDateString()}
                          </span>
                        )}
                        {accred.expiryDate && (
                          <span className={`flex items-center gap-1 ${
                            status === 'expired' ? 'text-red-400' : status === 'expiring' ? 'text-amber-400' : ''
                          }`}>
                            <Calendar size={12} /> {t('accreditations.expires')}: {new Date(accred.expiryDate).toLocaleDateString()}
                            {days !== null && (
                              <span className="ml-1">
                                ({days < 0 ? `${Math.abs(days)}d ${t('accreditations.overdue')}` : `${days}d ${t('accreditations.left')}`})
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                      {accred.notes && (
                        <p className="text-sm text-gray-400 mt-2 italic">{accred.notes}</p>
                      )}
                      {accred.fileUrl && (
                        <div className="mt-2 flex items-center gap-2">
                          <FileText size={14} className="text-primary" />
                          <a
                            href={accred.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline truncate"
                          >
                            {accred.fileName || t('accreditations.viewFile')}
                          </a>
                          <a href={accred.fileUrl} target="_blank" rel="noopener noreferrer" className="p-1 hover:bg-white/10 rounded text-gray-400">
                            <Eye size={14} />
                          </a>
                          <a href={accred.fileUrl} download className="p-1 hover:bg-white/10 rounded text-gray-400">
                            <Download size={14} />
                          </a>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => { setEditing(accred); setShowForm(true); }}
                        className="p-1.5 hover:bg-white/10 rounded text-gray-400 hover:text-primary"
                        title={t('common.edit')}
                      >
                        <Edit3 size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(accred)}
                        className="p-1.5 hover:bg-white/10 rounded text-gray-400 hover:text-red-400"
                        title={t('common.delete')}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Add / Edit Form */}
      {showForm && (
        <AccreditationForm
          accred={editing}
          companyId={companyId}
          staff={staff}
          onClose={() => { setShowForm(false); setEditing(null); }}
        />
      )}
    </div>
  );
}

// ── AccreditationForm ──────────────────────────────────────
function AccreditationForm({ accred, companyId, staff, onClose }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: accred?.name || '',
    issuer: accred?.issuer || '',
    certificateNumber: accred?.certificateNumber || '',
    issueDate: accred?.issueDate || '',
    expiryDate: accred?.expiryDate || '',
    notes: accred?.notes || '',
  });
  const [file, setFile] = useState(null);
  const [existingFile, setExistingFile] = useState(accred?.fileName || null);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      // 10MB limit
      if (f.size > 10 * 1024 * 1024) {
        alert(t('accreditations.fileTooLarge'));
        return;
      }
      setFile(f);
      setExistingFile(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    setSaving(true);

    try {
      let fileUrl = accred?.fileUrl || null;
      let fileName = accred?.fileName || null;
      let fileType = accred?.fileType || null;
      let filePath = accred?.filePath || null;

      // Upload new file if selected
      if (file) {
        const safeFileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
        const storagePath = `companies/${companyId}/accreditations/${staff.id}/${safeFileName}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        fileUrl = await getDownloadURL(storageRef);
        fileName = file.name;
        fileType = file.type;
        filePath = storagePath;

        // Delete old file if replacing
        if (accred?.filePath && accred.filePath !== storagePath) {
          try { await deleteObject(ref(storage, accred.filePath)); } catch (_) {}
        }
      }

      const payload = {
        name: formData.name.trim(),
        issuer: formData.issuer.trim(),
        certificateNumber: formData.certificateNumber.trim(),
        issueDate: formData.issueDate || null,
        expiryDate: formData.expiryDate || null,
        notes: formData.notes.trim(),
        fileUrl,
        fileName,
        fileType,
        filePath,
        updatedAt: new Date().toISOString(),
      };

      if (accred) {
        await updateDoc(doc(db, 'companies', companyId, 'staffAccreditations', accred.id), payload);
      } else {
        await addDoc(collection(db, 'companies', companyId, 'staffAccreditations'), {
          ...payload,
          userId: staff.id,
          staffName: staff.displayName || staff.username || '',
          createdAt: new Date().toISOString(),
        });
      }

      onClose();
    } catch (err) {
      console.error('Error saving accreditation:', err);
      alert(t('accreditations.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div
        className="bg-slate-800 border border-white/10 rounded-xl shadow-2xl max-w-md w-full p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-white">
            {accred ? t('accreditations.edit') : t('accreditations.add')}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded text-gray-300">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('accreditations.name')} *</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('accreditations.namePlaceholder')}
              className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-primary/50 focus:border-primary"
              required
            />
          </div>

          {/* Issuer */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('accreditations.issuer')}</label>
            <input
              type="text"
              value={formData.issuer}
              onChange={e => setFormData({ ...formData, issuer: e.target.value })}
              placeholder={t('accreditations.issuerPlaceholder')}
              className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>

          {/* Certificate Number */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('accreditations.certNumber')}</label>
            <input
              type="text"
              value={formData.certificateNumber}
              onChange={e => setFormData({ ...formData, certificateNumber: e.target.value })}
              placeholder={t('accreditations.certNumberPlaceholder')}
              className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('accreditations.issueDate')}</label>
              <input
                type="date"
                value={formData.issueDate}
                onChange={e => setFormData({ ...formData, issueDate: e.target.value })}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-primary/50 focus:border-primary [color-scheme:dark]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('accreditations.expiryDate')} *</label>
              <input
                type="date"
                value={formData.expiryDate}
                onChange={e => setFormData({ ...formData, expiryDate: e.target.value })}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-primary/50 focus:border-primary [color-scheme:dark]"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('accreditations.notes')}</label>
            <textarea
              value={formData.notes}
              onChange={e => setFormData({ ...formData, notes: e.target.value })}
              placeholder={t('accreditations.notesPlaceholder')}
              rows={2}
              className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-primary/50 focus:border-primary resize-none"
            />
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('accreditations.attachment')}</label>
            {existingFile && !file && (
              <div className="flex items-center gap-2 mb-2 text-sm text-gray-400">
                <FileText size={14} className="text-primary" />
                <span className="truncate">{existingFile}</span>
                <button type="button" onClick={() => setExistingFile(null)} className="text-red-400 hover:text-red-300 ml-auto">
                  <X size={14} />
                </button>
              </div>
            )}
            {file && (
              <div className="flex items-center gap-2 mb-2 text-sm text-gray-400">
                <FileText size={14} className="text-primary" />
                <span className="truncate">{file.name}</span>
                <button type="button" onClick={() => setFile(null)} className="text-red-400 hover:text-red-300 ml-auto">
                  <X size={14} />
                </button>
              </div>
            )}
            <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-dashed border-white/20 rounded-lg text-gray-400 hover:border-primary/50 hover:text-primary transition-colors text-sm"
            >
              <Upload size={16} />
              {file || existingFile ? t('accreditations.replaceFile') : t('accreditations.uploadFile')}
            </button>
            <p className="text-xs text-gray-500 mt-1">{t('accreditations.fileHint')}</p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-primary text-white rounded-lg py-2 font-medium hover:bg-primary-dark transition-colors disabled:opacity-50"
            >
              {saving ? t('common.loading') : t('common.save')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-white/10 text-gray-300 rounded-lg py-2 font-medium hover:bg-white/20 transition-colors"
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
